import React, { useState } from "react";
import { tr } from '@/i18n/tr';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageCircle, KeyRound, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import axios, { API } from "@/lib/api";

export default function ForgotPasswordModal({ open, onOpenChange }) {
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const close = (o) => {
    onOpenChange(o);
    if (!o) { setStep(1); setPhone(""); setCode(""); setPwd(""); setPwd2(""); setResult(null); }
  };

  const sendCode = async () => {
    if (!phone.trim()) { toast.error(tr('x_3e616117')); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { phone: phone.trim() });
      toast.success("Si el número está registrado, recibirás un código por WhatsApp");
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  const doReset = async () => {
    if (!code.trim()) { toast.error(tr('x_925ff2fa')); return; }
    if (pwd.length < 6) { toast.error(tr('x_798b1cb7')); return; }
    if (pwd !== pwd2) { toast.error(tr('x_8052ef2a')); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/reset-password`, { phone: phone.trim(), code: code.trim(), new_password: pwd });
      setResult(res.data);
      setStep(3);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-sm" data-testid="forgot-password-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-emerald-400" />
            {tr('x_fc14f592')}
          </DialogTitle>
        </DialogHeader>
        {step === 1 && (
          <div className="space-y-4">
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 text-sm text-emerald-300 flex items-start gap-2">
              <MessageCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Te enviaremos un código de 6 dígitos por WhatsApp al número registrado de tu tienda.<br />{tr('x_a850638e')}</span>
            </div>
            <div>
              <label className="text-sm text-slate-300 mb-1 block">{tr('x_6c546734')}</label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="0424 8476761"
                className="bg-slate-700/50 border-slate-600 text-white" data-testid="forgot-phone-input" />
            </div>
            <Button onClick={sendCode} disabled={loading}
              className="w-full bg-emerald-500 hover:bg-emerald-600" data-testid="forgot-send-code-btn">
              {loading ? "..." : tr('x_8049f1c7')}
            </Button>
          </div>
        )}
        {step === 2 && (
          <div className="space-y-4">
            <p className="text-sm text-slate-400">Código enviado por WhatsApp a <b className="text-white">{phone}</b> (válido 10 min)</p>
            <div>
              <label className="text-sm text-slate-300 mb-1 block">{tr('x_b3382444')}</label>
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" maxLength={6}
                className="bg-slate-700/50 border-slate-600 text-white text-center text-xl tracking-[0.5em]" data-testid="forgot-code-input" />
            </div>
            <div>
              <label className="text-sm text-slate-300 mb-1 block">{tr('x_a25bd352')}</label>
              <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)}
                className="bg-slate-700/50 border-slate-600 text-white" data-testid="forgot-new-password-input" />
            </div>
            <div>
              <label className="text-sm text-slate-300 mb-1 block">{tr('x_134cc560')}</label>
              <Input type="password" value={pwd2} onChange={(e) => setPwd2(e.target.value)}
                className="bg-slate-700/50 border-slate-600 text-white" data-testid="forgot-confirm-password-input" />
            </div>
            <Button onClick={doReset} disabled={loading}
              className="w-full bg-emerald-500 hover:bg-emerald-600" data-testid="forgot-reset-btn">
              {loading ? "..." : tr('x_9d936c0f')}
            </Button>
            <button onClick={() => setStep(1)} className="w-full text-xs text-slate-400 hover:text-white" data-testid="forgot-back-btn">
              {tr('x_2a86dd91')}
            </button>
          </div>
        )}
        {step === 3 && result && (
          <div className="space-y-4 text-center" data-testid="forgot-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
            <p className="text-emerald-300 font-medium">{tr('x_72138076')}</p>
            <div className="bg-slate-700/50 rounded-lg p-3 text-sm text-left space-y-1">
              <p><span className="text-slate-400">{tr('x_35973a26')}</span> <b className="text-white">{result.tenant_id}</b></p>
              <p><span className="text-slate-400">{tr('x_0c58a4d9')}</span> <b className="text-white">{result.username}</b></p>
            </div>
            <Button onClick={() => close(false)} className="w-full bg-emerald-500 hover:bg-emerald-600" data-testid="forgot-done-btn">
              {tr('x_49f38495')}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
